import { startTransition } from "react";
import type { FC } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "@privacy/translations";
import { Box, MenuItem, Select } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { multilineEllipsis } from "../../shared/multilineEllipsis";
import { DynamicGrid } from "../../dynamicGrid";
import { selectedSavedFilterId, selectedSavedFilterState } from "../../dynamicGrid/SavedFilters";
import { useGetRegistrationsGridView } from "../../api/registrations";
import type { GridViewRegistration } from "../../api/registrations.types";
import { useRegistrationVariant } from "./RegistrationVariantProvider";
import { useCurrentCustomer } from "../../root/CurrentOrganisationProvider";

type ControllerOption = { id: string; name: string; count: number };

const buildControllerOptions = (registrations: ReadonlyArray<GridViewRegistration>): Array<ControllerOption> => {
  const countsById = new Map<string, ControllerOption>();
  const countedInThisRegistration = new Set<string>();

  for (const registration of registrations) {
    countedInThisRegistration.clear();

    for (const { personalDataController } of registration.connectedTenants) {
      if (!personalDataController || countedInThisRegistration.has(personalDataController.id)) continue;

      countedInThisRegistration.add(personalDataController.id);

      const counted = countsById.get(personalDataController.id);

      if (counted) counted.count += 1;
      else countsById.set(personalDataController.id, { ...personalDataController, count: 1 });
    }
  }

  return [...countsById.values()];
};

const useControllerOptions = () => {
  const variant = useRegistrationVariant();
  const { language } = useTranslation();
  const registrationsGridView = useGetRegistrationsGridView(variant);

  const registrations = registrationsGridView.data?.registrations ?? [];

  return {
    options: buildControllerOptions(registrations).sort((a, b) => a.name.localeCompare(b.name, language)),
    totalCount: registrations.length,
  };
};

const CONTROLLER_QUERY_KEY = "controller";
const ALL_CONTROLLERS = "all";

const savedControllerId = (stringifiedFilters?: string) => {
  const state = selectedSavedFilterState(stringifiedFilters);

  return state && typeof state === "object" && "controllerId" in state && typeof state.controllerId === "string"
    ? state.controllerId
    : null;
};

export const scopeRegistrationsToController = (
  registrations: Array<GridViewRegistration>,
  controllerId: string | null
): Array<GridViewRegistration> =>
  controllerId === null
    ? registrations
    : registrations.flatMap(registration => {
        const connectedTenants = registration.connectedTenants.filter(
          tenant => tenant.personalDataController?.id === controllerId
        );

        return connectedTenants.length > 0 ? [{ ...registration, connectedTenants }] : [];
      });

export const useControllerFilter = (stringifiedFilters?: string) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { options, totalCount } = useControllerOptions();
  const variant = useRegistrationVariant();
  const currentCustomer = useCurrentCustomer();
  const showsFilter = currentCustomer.customerKind !== "enterprise" && variant === "Controller" && options.length > 1;

  const savedId = savedControllerId(stringifiedFilters);
  const requestedId = searchParams.get(CONTROLLER_QUERY_KEY);

  const findOption = (controllerId: string | null) => options.find(option => option.id === controllerId);

  const selected = (() => {
    if (!showsFilter) return undefined;
    if (requestedId === null) return findOption(savedId);
    if (requestedId === ALL_CONTROLLERS) return undefined;
    return findOption(requestedId) ?? findOption(savedId);
  })();

  const setRequestedId = (controllerId: string | null) =>
    setSearchParams(
      params => {
        if (controllerId === null) params.delete(CONTROLLER_QUERY_KEY);
        else params.set(CONTROLLER_QUERY_KEY, controllerId);
        return params;
      },
      { replace: true }
    );

  const followSavedView = () => {
    if (requestedId !== null) setRequestedId(null);
  };

  return {
    options,
    totalCount,
    showsFilter,
    selected,
    selectedControllerId: selected?.id ?? null,
    selectController: (controllerId: string | null) =>
      setRequestedId(controllerId ?? (savedId === null ? null : ALL_CONTROLLERS)),
    followSavedView,
    onSavedFiltersChange: (nextStringifiedFilters: string) => {
      const selectsAnotherView =
        selectedSavedFilterId(nextStringifiedFilters) !== selectedSavedFilterId(stringifiedFilters);

      if (selectsAnotherView || savedControllerId(nextStringifiedFilters) !== savedId) followSavedView();
    },
  };
};

export const ControllerFilterPicker: FC = () => {
  const { t } = useTranslation();
  const { savedFiltersProps } = DynamicGrid.useGridContext();
  const { options, totalCount, showsFilter, selected, selectedControllerId, selectController } = useControllerFilter(
    savedFiltersProps?.stringifiedFilters
  );

  const allControllersLabel = t("RegistrationGrid.ControllerFilter.All");

  if (!showsFilter) return null;

  const menuEntries = [
    {
      value: "",
      name: allControllersLabel,
      count: totalCount,
      countLabel: t(
        totalCount === 1
          ? "RegistrationGrid.ControllerFilter.TotalCountLabel.Singular"
          : "RegistrationGrid.ControllerFilter.TotalCountLabel.Plural"
      ),
    },
    ...options.map(option => ({
      value: option.id,
      name: option.name,
      count: option.count,
      countLabel: t(
        option.count === 1
          ? "RegistrationGrid.ControllerFilter.CountLabel.Singular"
          : "RegistrationGrid.ControllerFilter.CountLabel.Plural"
      ),
    })),
  ];

  return (
    <Select
      size="small"
      displayEmpty
      value={selectedControllerId ?? ""}
      onChange={event => startTransition(() => selectController(event.target.value || null))}
      renderValue={() => selected?.name ?? allControllersLabel}
      inputProps={{ "aria-label": t("RegistrationGrid.ControllerFilter.Label") }}
      sx={{ maxWidth: 400, fontSize: theme => theme.typography.pxToRem(12) }}
      MenuProps={{ slotProps: { paper: { sx: { minWidth: 280, maxWidth: 400, maxHeight: 320 } } } }}
    >
      {menuEntries.map(entry => (
        <MenuItem key={entry.value} value={entry.value} sx={{ gap: 4 }}>
          <Box sx={multilineEllipsis(1)}>{entry.name}</Box>
          <Box
            sx={{
              ml: "auto",
              fontSize: theme => theme.typography.pxToRem(12),
              color: theme => theme.palette.text.secondary,
            }}
          >
            {entry.count}
            <Box component="span" sx={visuallyHidden}>{` ${entry.countLabel}`}</Box>
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
};
